import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { Appointment, Notification } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import BookingStep3 from '../components/booking/BookingStep3';
import { ArrowLeft, Calendar, AlertTriangle, ClipboardList, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatLocalYmd, localDateAndTimeToUtcIso } from '../utils/timezoneUtils';
import { getAppointmentStartIso, getTotalDuration, hasCustomTimeBlocks } from '../utils/appointmentHelpers';
import { DEFAULT_BOOKING_SLOT_MINUTES, resolveBookingSlotMinutes } from '../utils/bookingSlotSettings';

const RescheduleAppointmentPage: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [timeConflict, setTimeConflict] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const [bookingSlotMinutes, setBookingSlotMinutes] = useState(DEFAULT_BOOKING_SLOT_MINUTES);
  
  // Extract professional IDs from appointment assignments
  const selectedProfessionals = React.useMemo(() => {
    if (!appointment?.assignments) return {};
    
    const professionals: { [serviceId: string]: any } = {};
    appointment.assignments.forEach(assignment => {
      if (assignment.professionalId && assignment.serviceId) {
        professionals[assignment.serviceId] = {
          id: assignment.professionalId,
          firstName: assignment.professionalFirstName,
          lastName: assignment.professionalLastName,
          email: assignment.professionalEmail
        };
      }
    });
    
    return professionals;
  }, [appointment]);
  
  const totalDuration = React.useMemo(() => getTotalDuration(appointment as any), [appointment]);

  const isCustomBlocks = React.useMemo(() => hasCustomTimeBlocks(appointment as any), [appointment]);

  const isFlexScheduled = React.useMemo(
    () => !!(appointment as any)?.flexScheduledAt?.earliest && (appointment as any)?.flexScheduledAt?.latest,
    [appointment]
  );

  const businessPhone = (appointment as any)?.business_phone ?? null;
  const cannotSelfReschedule = isCustomBlocks || isFlexScheduled;
  
  // Load appointment details
  useEffect(() => {
    const loadAppointment = async () => {
      if (!appointmentId) {
        setError('Appointment ID is required');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const appointmentData = await apiService.getAppointment(appointmentId);
        setAppointment(appointmentData);
        
        // Set initial selected date/time from current appointment
        const startIso = getAppointmentStartIso(appointmentData);
        if (startIso) {
          const scheduledDate = new Date(startIso);
          setSelectedDate(scheduledDate);
          
          // Format time as HH:MM
          const hours = scheduledDate.getHours().toString().padStart(2, '0');
          const minutes = scheduledDate.getMinutes().toString().padStart(2, '0');
          setSelectedTime(`${hours}:${minutes}`);
        }
      } catch (error: any) {
        console.error('Error loading appointment:', error);
        setError(error.response?.data?.message || 'Failed to load appointment details');
        toast.error('Failed to load appointment details');
      } finally {
        setLoading(false);
      }
    };
    
    loadAppointment();
  }, [appointmentId]);

  useEffect(() => {
    if (!appointment?.businessId) return;
    apiService
      .getAppointmentBookingSettings(appointment.businessId)
      .then((d) => setBookingSlotMinutes(resolveBookingSlotMinutes(d)))
      .catch(() => setBookingSlotMinutes(DEFAULT_BOOKING_SLOT_MINUTES));
  }, [appointment?.businessId]);

  // Load notifications for navbar
  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setUnreadNotifications([]);
        return;
      }
      
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        const unread = notifications.filter((n: Notification) => !n.is_read);
        setUnreadNotifications(unread);
      } catch (error) {
        console.error('Error loading notifications:', error);
        setUnreadNotifications([]);
      }
    };
    loadNotifications();
  }, [user?.id]);
  
  const handleReschedule = async () => {
    if (!appointment || !selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }

    if (isCustomBlocks) {
      toast.error('This appointment has custom service timing. Please contact the business to reschedule.');
      return;
    }
    
    if (timeConflict) {
      toast.error('The selected time has a conflict. Please choose another time.');
      return;
    }
    
    setSubmitting(true);
    try {
      // Customer selects date/time in *browser local timezone*; send an unambiguous UTC instant.
      const newScheduledAt = localDateAndTimeToUtcIso(selectedDate, selectedTime);

      // Call reschedule API
      await apiService.rescheduleAppointment(appointment.id, {
        scheduledAt: newScheduledAt
      });
      
      toast.success('Appointment rescheduled successfully!');
      
      // Navigate back to appointments page
      setTimeout(() => {
        navigate('/dashboard/customer/appointments?filter=upcoming');
      }, 1000);
    } catch (error: any) {
      console.error('Error rescheduling appointment:', error);
      toast.error(error.response?.data?.message || 'Failed to reschedule appointment');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar 
          onLogout={handleLogout}
          showDashboardLink={true}
          showLogoutButton={true}
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
        />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar 
          onLogout={handleLogout}
          showDashboardLink={true}
          showLogoutButton={true}
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
        />
        <div className="flex-1 pt-6 pb-12">
          <div className="px-4 md:px-8 max-w-4xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <h2 className="text-xl font-semibold text-red-900 mb-2">Error</h2>
              <p className="text-red-700 mb-4">{error || 'Appointment not found'}</p>
              <button
                onClick={() => navigate('/dashboard/customer/appointments')}
                className="aacore-btn-primary"
              >
                Back to Appointments
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      <CustomerNavbar 
        onLogout={handleLogout}
        showDashboardLink={true}
        showLogoutButton={true}
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />
      
      <div className="flex-1 min-w-0 pt-6 pb-12">
        <div className="px-4 lg:px-8 max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-4">
            <button
              onClick={() => navigate('/dashboard/customer/appointments')}
              className="flex items-center mb-4 font-semibold"
              style={{ color: '#0B4778' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Appointments
            </button>
            <h1 className="text-xl font-bold mb-0">Reschedule Appointment</h1>
            <p className="text-gray-600 text-sm mb-0">Select a new date and time for your appointment</p>
          </div>
          
          {/* Appointment Summary */}
          <div className="bg-blue-50 border border-blue-300 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Appointment Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-blue-900 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Date & Time</p>
                  <p className="font-medium text-gray-900">
                    {(() => {
                      const flex = (appointment as any)?.flexScheduledAt;
                      const iso = getAppointmentStartIso(appointment);
                      if (!iso) return 'Date TBD';
                      const formatDatePart = (s: string) =>
                        new Date(s).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                      const formatTimePart = (s: string) =>
                        new Date(s).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                      if (flex?.earliest && flex?.latest) {
                        const betweenStr = `Between ${formatTimePart(flex.earliest)} – ${formatTimePart(flex.latest)}`;
                        return (
                          <>
                            <span className="block sm:inline">{formatDatePart(flex.earliest)}</span>
                            <span className="block sm:inline">{' '}{betweenStr}</span>
                          </>
                        );
                      }
                      const d = new Date(iso);
                      return `${formatDatePart(iso)} at ${formatTimePart(iso)}`;
                    })()}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ClipboardList className="w-5 h-5 text-blue-900 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Services</p>
                  <p className="font-medium text-gray-900">
                    {appointment.assignments?.map(a => a.serviceName).join(', ') || 'N/A'}
                  </p>
                </div>
              </div>
              {appointment.business_name && (
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-blue-900 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Business</p>
                    <p className="font-medium text-gray-900">{appointment.business_name}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Contact business to reschedule (flex or custom timing) */}
          {isFlexScheduled && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-900 mb-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>
                This appointment has a flexible time window. To reschedule, please contact the business
                {businessPhone ? (
                  <> at <a href={`tel:${businessPhone}`} className="font-semibold underline">{businessPhone}</a></>
                ) : (
                  <> to reschedule</>
                )}
                .
              </span>
            </div>
          )}

          {isCustomBlocks && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-900 mb-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>This appointment has custom service timing. Please contact the business to reschedule.</span>
            </div>
          )}

          {/* Date and Time Selection (only when customer can self-reschedule) */}
          {!cannotSelfReschedule && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Select New Date & Time</h2>
              <BookingStep3
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onDateChange={setSelectedDate}
                onTimeChange={setSelectedTime}
                totalDuration={totalDuration}
                businessId={appointment.businessId}
                selectedProfessionals={selectedProfessionals}
                onTimeConflictChange={setTimeConflict}
                bookingSlotMinutes={bookingSlotMinutes}
              />
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-4 justify-end">
            <button
              onClick={() => navigate('/dashboard/customer/appointments')}
              className="aacore-btn-outlined"
            >
              Cancel
            </button>
            <button
              onClick={handleReschedule}
              disabled={!selectedDate || !selectedTime || timeConflict || submitting || cannotSelfReschedule}
              className="aacore-btn-primary"
            >
              {submitting ? (
                <span className="flex items-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Rescheduling...
                </span>
              ) : (
                'Confirm Reschedule'
              )}
            </button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default RescheduleAppointmentPage;

